import { useState, useEffect } from "react";
import ConsoleLayout from "@/components/ConsoleLayout";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { usersApi } from "@/lib/api";
import { adaptUsageRecord } from "@/lib/adapters";
import type { AdaptedUsageLog } from "@/lib/types";
import { UsageLogRow } from "@/components/UsageLogRow";
import { Search, Download, Filter, ChevronLeft, ChevronRight } from "lucide-react";
import { cn, exportToCsv } from "@/lib/utils";
import { useLanguage } from "@/contexts/LanguageContext";
import { DatePicker } from "@/components/ui/date-picker";

// Personal call-log page. Always scoped to the caller via /users/me/usage,
// regardless of team role — every user sees this view. Upstream / provider /
// per-dimension pricing are stripped server-side; only the ScienceLLM product
// key is exposed.
export default function ExperimentLogs() {
  const { t } = useLanguage();
  const { isAuthenticated } = useAuth();
  const [logs, setLogs] = useState<AdaptedUsageLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [searchField, setSearchField] = useState<"model" | "request_id">("model");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [appliedSearchField, setAppliedSearchField] = useState<"model" | "request_id">("model");
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const PAGE_SIZE = 50;

  const applyFilters = () => {
    setAppliedSearch(search);
    setAppliedSearchField(searchField);
    setPage(0);
  };

  const fetchLogs = async () => {
    if (!isAuthenticated) return;
    setLoading(true);
    try {
      const usage = await usersApi.getMyUsage({
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
        model: appliedSearchField === "model" && appliedSearch ? appliedSearch : undefined,
        request_id: appliedSearchField === "request_id" && appliedSearch ? appliedSearch : undefined,
        status: statusFilter || undefined,
        after: startDate ? new Date(startDate).toISOString() : undefined,
        before: endDate ? new Date(endDate + "T23:59:59").toISOString() : undefined,
      });
      setLogs(usage.records.map((r) => adaptUsageRecord(r, usage.api_key_names || {})));
      setTotal(usage.total);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, page, appliedSearch, appliedSearchField, statusFilter, startDate, endDate]);

  const totalPages = Math.ceil(total / PAGE_SIZE);
  const [exporting, setExporting] = useState(false);

  const handleExportCsv = async () => {
    if (total === 0) return;
    setExporting(true);
    try {
      const allUsage = await usersApi.getMyUsage({
        limit: total,
        offset: 0,
        model: appliedSearchField === "model" && appliedSearch ? appliedSearch : undefined,
        request_id: appliedSearchField === "request_id" && appliedSearch ? appliedSearch : undefined,
        status: statusFilter || undefined,
        after: startDate ? new Date(startDate).toISOString() : undefined,
        before: endDate ? new Date(endDate + "T23:59:59").toISOString() : undefined,
      });
      const allLogs = allUsage.records.map((r) => adaptUsageRecord(r, allUsage.api_key_names || {}));
      exportToCsv(
        `call-logs-${new Date().toISOString().slice(0, 10)}.csv`,
        ["Request ID", "Model", "Type", "Status", "Timestamp", "Input Tokens", "Output Tokens", "Cache Read Tokens", "Cache Write Tokens", "Total Tokens", "Latency (ms)", "Cost (ST)", "API Key", "Error"],
        allLogs.map((l) => [l.requestId, l.model, l.type, l.status, l.timestamp, String(l.inputTokens || 0), String(l.outputTokens || 0), String(l.cacheReadTokens || 0), String(l.cacheWriteTokens || 0), String(l.tokens), String(l.latency), l.cost, l.keyName, l.errorMessage || ""])
      );
    } catch {
      // ignore
    } finally {
      setExporting(false);
    }
  };

  return (
    <ConsoleLayout
      title={t("logs.title")}
      subtitle={t("logs.subtitle")}
      actions={
        <Button size="sm" variant="ghost" className="gap-1.5 text-xs h-8 text-slate-400 border border-white/10" onClick={handleExportCsv} disabled={total === 0 || exporting}>
          <Download size={12} />
          {exporting ? "..." : t("logs.exportCsv")}
        </Button>
      }
    >
      <div className="p-6 space-y-5">
        {/* Filters */}
        <div className="flex items-center gap-3 flex-wrap">
          <select
            value={searchField}
            onChange={(e) => setSearchField(e.target.value as "model" | "request_id")}
            className="bg-white/5 border border-white/10 rounded-lg px-2 py-1.5 text-xs text-white h-9 focus:outline-none focus:border-blue-500/50"
          >
            <option value="model">{t("logs.searchByModel")}</option>
            <option value="request_id">{t("logs.searchByRequestId")}</option>
          </select>
          <div className="relative flex-1 min-w-[200px] max-w-[300px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <Input
              placeholder={searchField === "model" ? t("logs.searchModelPlaceholder") : t("logs.searchRequestIdPlaceholder")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") applyFilters(); }}
              maxLength={searchField === "request_id" ? 26 : undefined}
              className={cn("pl-9 bg-white/5 border-white/10 text-sm h-9 text-white placeholder:text-slate-600", searchField === "request_id" && "font-mono")}
            />
          </div>
          <Button size="sm" variant="ghost" onClick={applyFilters} className="h-9 px-3 text-xs text-slate-300 border border-white/10">
            {t("logs.applyFilters")}
          </Button>
          <div className="flex items-center gap-1.5">
            <Filter size={12} className="text-slate-600" />
            {["success", "failed", "running"].map((s) => (
              <button
                key={s}
                onClick={() => { setStatusFilter(statusFilter === s ? null : s); setPage(0); }}
                className={cn(
                  "text-xs px-2.5 py-1 rounded-lg transition-all capitalize",
                  statusFilter === s ? "bg-white/10 text-white" : "text-slate-500 hover:text-slate-300"
                )}
              >
                {s}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <DatePicker value={startDate} onChange={(v) => { setStartDate(v); setPage(0); }} />
            <span className="text-xs text-slate-600">—</span>
            <DatePicker value={endDate} onChange={(v) => { setEndDate(v); setPage(0); }} />
          </div>
          <div className="ml-auto flex items-center gap-3">
            <span className="text-xs text-slate-500">{total} {t("logs.records")}</span>
            {totalPages > 1 && (
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setPage(Math.max(0, page - 1))}
                  disabled={page === 0}
                  className="p-1 rounded text-slate-500 hover:text-white disabled:opacity-30"
                >
                  <ChevronLeft size={14} />
                </button>
                <span className="text-xs text-slate-400">{page + 1} / {totalPages}</span>
                <button
                  onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
                  disabled={page >= totalPages - 1}
                  className="p-1 rounded text-slate-500 hover:text-white disabled:opacity-30"
                >
                  <ChevronRight size={14} />
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Logs Table */}
        <div className="sci-card overflow-hidden">
          <div className="px-5 py-3 border-b border-white/8 grid grid-cols-[24px_1fr_120px_80px_20px] gap-4 text-[10px] text-slate-600 uppercase tracking-wider items-center">
            <div />
            <div>{t("logs.modelRequest")}</div>
            <div className="hidden sm:block text-right">{t("logs.usage")}</div>
            <div className="text-right">{t("logs.cost")}</div>
            <div />
          </div>
          {logs.length > 0 ? (
            logs.map((log) => <UsageLogRow key={log.id} log={log} />)
          ) : (
            <div className="py-16 text-center text-slate-500 text-sm">
              {loading ? "Loading..." : t("logs.noLogs")}
            </div>
          )}
        </div>
      </div>
    </ConsoleLayout>
  );
}
